import { useState, useRef, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { TimeInput } from '../components/ui/time-input';
import { AddressAutocomplete, ParsedAddress } from '../components/AddressAutocomplete';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { useClientAuth } from '../client/hooks/useClientAuth';

const CREATE_INQUIRY = gql`
  mutation CreateInquiry($input: CreateInquiryInput!) {
    createInquiry(input: $input) {
      id
      firstName
      lastName
      email
      phoneNumber
      status
      userId
      existingAccountUsed
      createdAt
    }
  }
`;

const CHECK_EMAIL_EXISTS = gql`
  query CheckEmailExists($email: String!) {
    checkEmailExists(email: $email)
  }
`;

const GET_INQUIRY_ATTACHMENT_UPLOAD_URL = gql`
  mutation GetInquiryAttachmentUploadUrl($input: InquiryAttachmentUploadInput!) {
    getInquiryAttachmentUploadUrl(input: $input) {
      uploadUrl
      s3Key
    }
  }
`;

// Type definitions for Apollo mutations
interface CreatedInquiry {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  status: string;
  userId: string | null;
  existingAccountUsed: boolean | null;
  createdAt: string;
}

interface CreateInquiryData {
  createInquiry: CreatedInquiry;
}

interface GetInquiryAttachmentUploadUrlData {
  getInquiryAttachmentUploadUrl: {
    uploadUrl: string;
    s3Key: string;
  };
}

interface FormData {
  // Contact Information
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  preferredContactMethod: string;
  bestTimeToContact: string;
  // Address Information
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  // Incident Details
  incidentType: string;
  incidentDate: string;
  incidentTime: string;
  incidentLocation: string;
  weatherConditions: string[];
  description: string;
  // Injuries & Medical Treatment
  injuryTypes: string[];
  injurySeverity: string;
  medicalAttentionSought: boolean | null;
  medicalAttentionTiming: string;
  treatmentLocation: string;
  ongoingTreatment: boolean | null;
  hasHealthInsurance: boolean | null;
  // Insurance & Legal Status
  policeReportFiled: boolean | null;
  policeReportNumber: string;
  atFault: string;
  otherPartyInsured: boolean | null;
  otherPartyInsurance: string;
  contactedByInsurance: boolean | null;
  hasAttorney: boolean | null;
  claimOrLawsuitFiled: string;
  // Additional Information
  hasPhotosVideos: boolean | null;
  attachments: Array<{
    fileName: string;
    fileType: string;
    fileSize: number;
    s3Key: string;
  }>;
  // Account Creation
  createAccount: boolean;
  password: string;
  confirmPassword: string;
}

export function InquiryIntakeForm() {
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated } = useClientAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [addressSearchValue, setAddressSearchValue] = useState('');
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkEmailExists] = useLazyQuery<{ checkEmailExists: boolean }>(CHECK_EMAIL_EXISTS);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    preferredContactMethod: 'AA Law App',
    bestTimeToContact: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    incidentType: '',
    incidentDate: '',
    incidentTime: '',
    incidentLocation: '',
    weatherConditions: [],
    description: '',
    injuryTypes: [],
    injurySeverity: '',
    medicalAttentionSought: null,
    medicalAttentionTiming: '',
    treatmentLocation: '',
    ongoingTreatment: null,
    hasHealthInsurance: null,
    policeReportFiled: null,
    policeReportNumber: '',
    atFault: '',
    otherPartyInsured: null,
    otherPartyInsurance: '',
    contactedByInsurance: null,
    hasAttorney: null,
    claimOrLawsuitFiled: '',
    hasPhotosVideos: null,
    attachments: [],
    createAccount: true,
    password: '',
    confirmPassword: '',
  });

  // Pre-fill form data if user is logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData((prev) => ({
        ...prev,
        firstName: user.firstName || prev.firstName,
        lastName: user.lastName || prev.lastName,
        email: user.email || prev.email,
        phoneNumber: user.phoneNumber || prev.phoneNumber,
        // Pre-fill address if available
        street1: user.address?.street1 || prev.street1,
        street2: user.address?.street2 || prev.street2,
        city: user.address?.city || prev.city,
        state: user.address?.state || prev.state,
        zipCode: user.address?.zipCode || prev.zipCode,
        createAccount: false, // Don't show account creation for logged-in users
      }));
      
      // Also set the address search value if address is available
      if (user.address?.street1 && user.address?.city && user.address?.state) {
        const fullAddress = [
          user.address.street1,
          user.address.street2,
          user.address.city,
          user.address.state,
          user.address.zipCode
        ].filter(Boolean).join(', ');
        setAddressSearchValue(fullAddress);
      }
    }
  }, [isAuthenticated, user]);

  const [createInquiry, { loading }] = useMutation<CreateInquiryData>(CREATE_INQUIRY);
  const [getUploadUrl] = useMutation<GetInquiryAttachmentUploadUrlData>(GET_INQUIRY_ATTACHMENT_UPLOAD_URL);

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Reset email exists check when email changes
    if (field === 'email') {
      setEmailExists(null);
    }
  };

  // Check if email exists when user enables account creation on Step 6
  const handleCheckEmail = async () => {
    if (!formData.email || !isValidEmail(formData.email)) return;
    
    setCheckingEmail(true);
    try {
      const result = await checkEmailExists({ variables: { email: formData.email } });
      setEmailExists(result.data?.checkEmailExists ?? false);
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailExists(null);
    } finally {
      setCheckingEmail(false);
    }
  };

  // Format phone number as user types: (555) 123-4567
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limited = digits.slice(0, 10);
    
    // Format based on length
    if (limited.length === 0) return '';
    if (limited.length <= 3) return `(${limited}`;
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateFormData('phoneNumber', formatted);
  };

  const handleAddressSelect = (address: ParsedAddress) => {
    setFormData((prev) => ({
      ...prev,
      street1: address.street1,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
    }));
    setAddressSearchValue(address.fullAddress);
  };

  const toggleArrayValue = (field: keyof FormData, value: string) => {
    setFormData((prev) => {
      const currentArray = prev[field] as string[];
      const newArray = currentArray.includes(value)
        ? currentArray.filter((v) => v !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploadingFiles(true);

    try {
      const uploadedAttachments: Array<{
        fileName: string;
        fileType: string;
        fileSize: number;
        s3Key: string;
      }> = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file size (50MB max)
        if (file.size > 50 * 1024 * 1024) {
          alert(`File ${file.name} is too large. Maximum size is 50MB.`);
          continue;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
          alert(`File ${file.name} has an unsupported type. Allowed: JPEG, PNG, PDF, DOCX`);
          continue;
        }

        // Get pre-signed upload URL
        const { data } = await getUploadUrl({
          variables: {
            input: {
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
            },
          },
        });

        if (!data?.getInquiryAttachmentUploadUrl) {
          throw new Error('Failed to get upload URL');
        }

        const { uploadUrl, s3Key } = data.getInquiryAttachmentUploadUrl;

        // Upload file to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        uploadedAttachments.push({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          s3Key,
        });
      }

      // Add uploaded attachments to form data
      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedAttachments],
        hasPhotosVideos: true,
      }));
    } catch (error: any) {
      console.error('Error uploading files:', error);
      alert('Error uploading files: ' + error.message);
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeAttachment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
      hasPhotosVideos: prev.attachments.length > 1,
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validate password if creating account
      if (formData.createAccount) {
        const { isValid, errors } = validatePassword(formData.password);
        if (!isValid) {
          alert('Password requirements not met:\n• ' + errors.join('\n• '));
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          alert('Passwords do not match');
          return;
        }
      }
      
      // Build input object - send empty strings for blank fields instead of omitting them
      const input: any = {
        // Required fields
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        incidentType: formData.incidentType,
        description: formData.description,
        
        // Address object - only include if street1 is provided
        address: formData.street1 ? {
          street1: formData.street1,
          street2: formData.street2 || '',
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
        } : undefined,
        
        // Optional string fields - send empty string if blank
        preferredContactMethod: formData.preferredContactMethod || '',
        bestTimeToContact: formData.bestTimeToContact || '',
        incidentDate: formData.incidentDate || '',
        incidentTime: formData.incidentTime || '',
        incidentLocation: formData.incidentLocation || '',
        injurySeverity: formData.injurySeverity || '',
        medicalAttentionTiming: formData.medicalAttentionTiming || '',
        treatmentLocation: formData.treatmentLocation || '',
        policeReportNumber: formData.policeReportNumber || '',
        atFault: formData.atFault || '',
        otherPartyInsurance: formData.otherPartyInsurance || '',
        claimOrLawsuitFiled: formData.claimOrLawsuitFiled || '',
        
        // Array fields - send empty array if not set
        weatherConditions: formData.weatherConditions || [],
        injuryTypes: formData.injuryTypes || [],
        
        // Boolean fields - send false if null
        medicalAttentionSought: formData.medicalAttentionSought === true,
        ongoingTreatment: formData.ongoingTreatment === true,
        hasHealthInsurance: formData.hasHealthInsurance === true,
        policeReportFiled: formData.policeReportFiled === true,
        otherPartyInsured: formData.otherPartyInsured === true,
        contactedByInsurance: formData.contactedByInsurance === true,
        hasAttorney: formData.hasAttorney === true,
        hasPhotosVideos: formData.attachments.length > 0,
        
        // Account creation fields
        createAccount: formData.createAccount,
        password: formData.createAccount ? formData.password : undefined,
        
        // Attachments array - only include if there are attachments
        attachments: formData.attachments.length > 0 ? formData.attachments.map(att => ({
          fileName: att.fileName,
          fileType: att.fileType,
          fileSize: att.fileSize,
          s3Key: att.s3Key,
        })) : [],
      };

      console.log('Submitting inquiry with input:', JSON.stringify(input, null, 2));

      const result = await createInquiry({
        variables: { input },
      });

      console.log('Mutation result:', JSON.stringify(result, null, 2));

      if (result.data?.createInquiry) {
        console.log('Inquiry created successfully:', result.data.createInquiry);
        const inquiry = result.data.createInquiry;
        
        // Determine the appropriate success message
        if (formData.createAccount && inquiry.existingAccountUsed) {
          // User tried to create account but email already exists - inquiry still submitted
          alert('Inquiry submitted successfully!\n\nNote: An account with this email already exists. Please log in with your existing credentials to track your inquiry. Our team will be in touch shortly.');
        } else if (formData.createAccount && inquiry.userId) {
          // New account created successfully
          alert('Inquiry submitted and account created successfully!\n\nYou can now log in with your email and password to track your case. Our team will be in touch shortly.');
        } else if (formData.createAccount && !inquiry.userId) {
          // User requested account but it wasn't created (likely an error occurred)
          alert('Inquiry submitted successfully!\n\nHowever, there was an issue creating your account. Please contact us or try creating an account later. Our team will be in touch shortly.');
        } else {
          // User didn't request account creation
          alert('Inquiry submitted successfully!\n\nOur team will be in touch shortly to discuss your case.');
        }
        navigate('/');
      } else if (result.error) {
        console.error('GraphQL error:', result.error);
        alert('Error submitting inquiry: ' + result.error.message);
      } else {
        console.error('Unknown error - result:', JSON.stringify(result, null, 2));
        alert('Error submitting inquiry: Unknown error occurred. Please check the console.');
      }
    } catch (error: any) {
      console.error('Error submitting inquiry:', error);
      alert('Error submitting inquiry: ' + (error.message || JSON.stringify(error)));
    }
  };

  const nextStep = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
      // Scroll to top of form when moving to next step
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top of form when moving to previous step
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    // Stricter email regex - only allows alphanumeric, dots, hyphens, underscores, and plus signs
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Password validation helper - must match Cognito policy
  const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) errors.push('One special character');
    return { isValid: errors.length === 0, errors };
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.firstName && formData.lastName && 
               formData.email && isValidEmail(formData.email) && 
               formData.phoneNumber && 
               formData.street1 && formData.city && formData.state && formData.zipCode;
      case 2:
        return formData.incidentType && formData.incidentLocation && formData.description;
      case 3:
        return formData.injuryTypes.length > 0 && formData.injurySeverity && formData.medicalAttentionSought !== null;
      case 4:
        return formData.policeReportFiled !== null && formData.atFault && formData.hasAttorney !== null;
      case 5:
        // Step 5 is just additional info/file uploads - always can proceed
        return true;
      case 6:
        // If logged in, no account creation needed
        if (isAuthenticated) {
          return true;
        }
        // If creating account, validate password fields and check email doesn't exist
        if (formData.createAccount) {
          const { isValid } = validatePassword(formData.password);
          // Block if email exists (user should sign in instead)
          if (emailExists === true) {
            return false;
          }
          return isValid && formData.password === formData.confirmPassword;
        }
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12 px-3 sm:px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <img 
            src="/icon.png" 
            alt="AA Law Logo" 
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg mx-auto mb-3 sm:mb-4 shadow-md"
          />
          <h1 className="text-2xl sm:text-4xl font-bold text-aalaw-charcoal mb-2">Get Help Now</h1>
          <p className="text-base sm:text-lg text-gray-600">Free Case Evaluation • No Win, No Fee</p>
          <div className="mt-3 sm:mt-4 inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-gold-50 border border-gold-200 rounded-lg">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gold-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
            <span className="text-xs sm:text-sm font-semibold text-gray-900">Over $100 Million Recovered</span>
          </div>
          
          {/* Sign in link for users with existing accounts */}
          {!isAuthenticated && (
            <div className="mt-4 text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/signin" className="text-aalaw-red font-semibold hover:underline">
                Sign in
              </Link>
              {' '}to track your existing inquiries or submit a new one.
            </div>
          )}
          
          {/* Logged in indicator */}
          {isAuthenticated && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link 
                to="/client/inquiry" 
                className="inline-flex items-center gap-1 text-sm text-aalaw-red font-semibold hover:underline transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Portal
              </Link>
              <div className="inline-flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium text-green-800">
                  Signed in as {user?.firstName} {user?.lastName}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Progress Indicator */}
        <div className="mb-6 sm:mb-8">
          {/* Mobile: Simple progress bar */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Step {currentStep} of 6</span>
              <span className="text-sm text-gray-500">{Math.round((currentStep / 6) * 100)}% complete</span>
            </div>
            <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-aalaw-red rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 6) * 100}%` }}
              />
            </div>
            <div className="flex justify-between mt-3">
              {[1, 2, 3, 4, 5, 6].map((step) => (
                <div 
                  key={step}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                    step === currentStep
                      ? 'bg-aalaw-red text-white ring-2 ring-red-200'
                      : step < currentStep
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step < currentStep ? '✓' : step}
                </div>
              ))}
            </div>
          </div>

          {/* Desktop: Full stepper */}
          <div className="hidden sm:block">
            <div className="flex items-center justify-center">
              {[1, 2, 3, 4, 5, 6].map((step, index) => (
                <div key={step} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-semibold text-sm md:text-base transition-all ${
                        step === currentStep
                          ? 'bg-aalaw-red text-white ring-4 ring-red-100 shadow-lg'
                          : step < currentStep
                          ? 'bg-green-500 text-white shadow-md'
                          : 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                      }`}
                    >
                      {step < currentStep ? (
                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : step}
                    </div>
                  </div>
                  {index < 5 && (
                    <div className={`w-8 md:w-16 lg:w-24 h-1 mx-1 md:mx-2 rounded-full transition-all ${
                      step < currentStep ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-gray-600 mt-4">Step {currentStep} of 6</p>
          </div>
        </div>

        {/* Form Card */}
        <Card ref={formRef} className="shadow-sm sm:shadow-md">
          <CardContent className="p-4 sm:p-8">

            {/* Step 1: Contact Information */}
            {currentStep === 1 && (
              <div className="space-y-5 sm:space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Contact Information</h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                    <Input
                      value={formData.firstName}
                      onChange={(e) => updateFormData('firstName', e.target.value)}
                      placeholder="John"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) => updateFormData('lastName', e.target.value)}
                      placeholder="Doe"
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                  <Input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    placeholder="john.doe@example.com"
                    className={`w-full ${
                      formData.email && !isValidEmail(formData.email) 
                        ? 'border-red-300 focus:ring-red-500' 
                        : formData.email && isValidEmail(formData.email)
                          ? 'border-green-300'
                          : ''
                    }`}
                  />
                  {formData.email && !isValidEmail(formData.email) ? (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Please enter a valid email address
                    </p>
                  ) : (
                    <p className="text-xs text-gray-600 mt-1">We'll send you a confirmation immediately</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Contact Method *</label>
                  <div className="space-y-2">
                    {[
                      { value: 'AA Law App', label: 'AA Law App', badge: '⭐ Recommended', highlight: true },
                      { value: 'Phone Call', label: 'Phone Call' },
                      { value: 'Text Message', label: 'Text Message' },
                      { value: 'Email', label: 'Email' },
                    ].map((method) => (
                      <label 
                        key={method.value} 
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                          method.highlight 
                            ? 'border-aalaw-red bg-red-50 hover:bg-red-100' 
                            : 'border-gray-200 hover:bg-gray-50'
                        } ${formData.preferredContactMethod === method.value ? 'ring-2 ring-aalaw-red' : ''}`}
                      >
                        <input
                          type="radio"
                          name="contactMethod"
                          value={method.value}
                          checked={formData.preferredContactMethod === method.value}
                          onChange={(e) => updateFormData('preferredContactMethod', e.target.value)}
                          className="w-4 h-4 text-aalaw-red"
                        />
                        <span className="text-sm font-medium text-gray-900">{method.label}</span>
                        {method.badge && (
                          <span className="ml-auto text-xs font-semibold text-aalaw-red bg-white px-2 py-1 rounded-full border border-aalaw-red">
                            {method.badge}
                          </span>
                        )}
                      </label>
                    ))}
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Get instant updates, message your team, and track your case progress with our app
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Your Address *</label>
                  <AddressAutocomplete
                    value={addressSearchValue}
                    onChange={setAddressSearchValue}
                    onAddressSelect={handleAddressSelect}
                    placeholder="Start typing your address..."
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Search for your address or enter manually below</p>
                </div>

                {/* Show parsed address fields - editable */}
                {(formData.street1 || formData.city || formData.state || formData.zipCode) && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-4">
                    <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Address found - verify and edit if needed
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Street Address</label>
                      <Input
                        value={formData.street1}
                        onChange={(e) => updateFormData('street1', e.target.value)}
                        placeholder="123 Main Street"
                        className="w-full bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Apartment, Suite, etc. (Optional)</label>
                      <Input
                        value={formData.street2}
                        onChange={(e) => updateFormData('street2', e.target.value)}
                        placeholder="Apt 4B"
                        className="w-full bg-white"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">City</label>
                        <Input
                          value={formData.city}
                          onChange={(e) => updateFormData('city', e.target.value)}
                          placeholder="City"
                          className="w-full bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">State</label>
                        <Input
                          value={formData.state}
                          onChange={(e) => updateFormData('state', e.target.value)}
                          placeholder="CA"
                          maxLength={2}
                          className="w-full bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Zip Code</label>
                        <Input
                          value={formData.zipCode}
                          onChange={(e) => updateFormData('zipCode', e.target.value)}
                          placeholder="12345"
                          maxLength={5}
                          className="w-full bg-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Manual entry option when no address selected */}
                {!formData.street1 && !formData.city && !formData.state && !formData.zipCode && (
                  <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                    <p className="text-sm text-gray-600">Or enter your address manually:</p>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Street Address *</label>
                      <Input
                        value={formData.street1}
                        onChange={(e) => updateFormData('street1', e.target.value)}
                        placeholder="123 Main Street"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Apartment, Suite, etc. (Optional)</label>
                      <Input
                        value={formData.street2}
                        onChange={(e) => updateFormData('street2', e.target.value)}
                        placeholder="Apt 4B"
                        className="w-full"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                        <Input
                          value={formData.city}
                          onChange={(e) => updateFormData('city', e.target.value)}
                          placeholder="City"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">State *</label>
                        <select
                          value={formData.state}
                          onChange={(e) => updateFormData('state', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                        >
                          <option value="">Select state</option>
                          <option value="AL">Alabama</option>
                          <option value="AK">Alaska</option>
                          <option value="AZ">Arizona</option>
                          <option value="AR">Arkansas</option>
                          <option value="CA">California</option>
                          <option value="CO">Colorado</option>
                          <option value="CT">Connecticut</option>
                          <option value="DE">Delaware</option>
                          <option value="FL">Florida</option>
                          <option value="GA">Georgia</option>
                          <option value="HI">Hawaii</option>
                          <option value="ID">Idaho</option>
                          <option value="IL">Illinois</option>
                          <option value="IN">Indiana</option>
                          <option value="IA">Iowa</option>
                          <option value="KS">Kansas</option>
                          <option value="KY">Kentucky</option>
                          <option value="LA">Louisiana</option>
                          <option value="ME">Maine</option>
                          <option value="MD">Maryland</option>
                          <option value="MA">Massachusetts</option>
                          <option value="MI">Michigan</option>
                          <option value="MN">Minnesota</option>
                          <option value="MS">Mississippi</option>
                          <option value="MO">Missouri</option>
                          <option value="MT">Montana</option>
                          <option value="NE">Nebraska</option>
                          <option value="NV">Nevada</option>
                          <option value="NH">New Hampshire</option>
                          <option value="NJ">New Jersey</option>
                          <option value="NM">New Mexico</option>
                          <option value="NY">New York</option>
                          <option value="NC">North Carolina</option>
                          <option value="ND">North Dakota</option>
                          <option value="OH">Ohio</option>
                          <option value="OK">Oklahoma</option>
                          <option value="OR">Oregon</option>
                          <option value="PA">Pennsylvania</option>
                          <option value="RI">Rhode Island</option>
                          <option value="SC">South Carolina</option>
                          <option value="SD">South Dakota</option>
                          <option value="TN">Tennessee</option>
                          <option value="TX">Texas</option>
                          <option value="UT">Utah</option>
                          <option value="VT">Vermont</option>
                          <option value="VA">Virginia</option>
                          <option value="WA">Washington</option>
                          <option value="WV">West Virginia</option>
                          <option value="WI">Wisconsin</option>
                          <option value="WY">Wyoming</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Zip Code *</label>
                      <Input
                        value={formData.zipCode}
                        onChange={(e) => updateFormData('zipCode', e.target.value)}
                        placeholder="12345"
                        maxLength={5}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Incident Details */}
            {currentStep === 2 && (
              <div className="space-y-5 sm:space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Incident Details</h2>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type of Incident *</label>
                  <select
                    value={formData.incidentType}
                    onChange={(e) => updateFormData('incidentType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  >
                    <option value="">Select incident type</option>
                    <option value="Car Accident">Car Accident</option>
                    <option value="Motorcycle Accident">Motorcycle Accident</option>
                    <option value="Truck Accident">Truck Accident</option>
                    <option value="Pedestrian Accident">Pedestrian Accident</option>
                    <option value="Bicycle Accident">Bicycle Accident</option>
                    <option value="Slip and Fall">Slip and Fall</option>
                    <option value="Medical Malpractice">Medical Malpractice</option>
                    <option value="Workers Compensation">Workers' Compensation</option>
                    <option value="Dog Bite">Dog Bite</option>
                    <option value="Premises Liability">Premises Liability</option>
                    <option value="Defective Product">Defective Product</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Incident</label>
                    <DateInput
                      value={formData.incidentDate}
                      onChange={(e) => updateFormData('incidentDate', e.target.value)}
                      placeholder="Select incident date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Time of Incident</label>
                    <TimeInput
                      value={formData.incidentTime}
                      onChange={(e) => updateFormData('incidentTime', e.target.value)}
                      placeholder="Select incident time"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Location of Incident *</label>
                  <LocationAutocomplete
                    value={formData.incidentLocation}
                    onChange={(value) => updateFormData('incidentLocation', value)}
                    placeholder="Search for incident location..."
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Start typing to search for the address or location name</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Brief Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                    placeholder="Please describe what happened in 3-5 sentences..."
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  />
                  <p className="text-xs text-gray-600 mt-1">{formData.description.length} / 500 characters</p>
                </div>
              </div>
            )}

            {/* Step 3: Injuries & Medical Treatment */}
            {currentStep === 3 && (
              <div className="space-y-5 sm:space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Injuries & Medical Treatment</h2>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type of Injuries * (Select all that apply)</label>
                  <div className="space-y-2">
                    {[
                      'Head/Brain Injury',
                      'Neck/Back Injury',
                      'Spinal Cord Injury',
                      'Broken Bones/Fractures',
                      'Soft Tissue Injury',
                      'Burns',
                      'Internal Injuries',
                      'Scarring/Disfigurement',
                      'Emotional/Psychological Trauma',
                      'Other',
                    ].map((injury) => (
                      <label key={injury} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.injuryTypes.includes(injury)}
                          onChange={() => toggleArrayValue('injuryTypes', injury)}
                          className="w-4 h-4 text-aalaw-red"
                        />
                        <span className="text-sm font-medium text-gray-900">{injury}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Severity of Injuries *</label>
                  <div className="space-y-2">
                    {[
                      { value: 'Minor', label: 'Minor (treated and released)' },
                      { value: 'Moderate', label: 'Moderate (required follow-up care)' },
                      { value: 'Serious', label: 'Serious (hospitalized)' },
                      { value: 'Severe', label: 'Severe (ongoing treatment/disability)' },
                    ].map((severity) => (
                      <label key={severity.value} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="severity"
                          value={severity.value}
                          checked={formData.injurySeverity === severity.value}
                          onChange={(e) => updateFormData('injurySeverity', e.target.value)}
                          className="w-4 h-4 text-aalaw-red"
                        />
                        <span className="text-sm font-medium text-gray-900">{severity.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Did you seek medical attention? *</label>
                  <div className="space-y-2">
                    {[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ].map((option) => (
                      <label key={String(option.value)} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="medicalAttention"
                          checked={formData.medicalAttentionSought === option.value}
                          onChange={() => updateFormData('medicalAttentionSought', option.value)}
                          className="w-4 h-4 text-aalaw-red"
                        />
                        <span className="text-sm font-medium text-gray-900">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {formData.medicalAttentionSought && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Where did you receive treatment?</label>
                    <Input
                      value={formData.treatmentLocation}
                      onChange={(e) => updateFormData('treatmentLocation', e.target.value)}
                      placeholder="Hospital name or clinic"
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Insurance & Legal Status */}
            {currentStep === 4 && (
              <div className="space-y-5 sm:space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Insurance & Legal Status</h2>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Was a police report filed? *</label>
                  <div className="space-y-2">
                    {[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ].map((option) => (
                      <label key={String(option.value)} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="policeReport"
                          checked={formData.policeReportFiled === option.value}
                          onChange={() => updateFormData('policeReportFiled', option.value)}
                          className="w-4 h-4 text-aalaw-red"
                        />
                        <span className="text-sm font-medium text-gray-900">{option.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-2">Police reports are critical for your case</p>
                </div>

                {formData.policeReportFiled && (
                  <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Police Report Number (if known)</label>
                      <Input
                        value={formData.policeReportNumber}
                        onChange={(e) => updateFormData('policeReportNumber', e.target.value)}
                        placeholder="Report number"
                        className="w-full bg-white"
                      />
                    </div>
                    
                    {/* Police Report Upload */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Upload Police Report (Optional)
                      </label>
                      <p className="text-xs text-gray-600 mb-2">
                        If you have a copy of the police report, upload it here for faster processing.
                      </p>
                      <div
                        className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center hover:border-aalaw-red transition-colors cursor-pointer bg-white"
                        onClick={() => document.getElementById('police-report-input')?.click()}
                      >
                        <div className="flex flex-col items-center">
                          <svg className="w-8 h-8 text-blue-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-sm font-medium text-gray-700">
                            {uploadingFiles ? 'Uploading...' : 'Click to upload police report'}
                          </p>
                          <p className="text-xs text-gray-500">PDF, JPEG, PNG (Max 50MB)</p>
                        </div>
                        <input
                          id="police-report-input"
                          type="file"
                          accept="image/jpeg,image/png,application/pdf"
                          onChange={(e) => handleFileUpload(e.target.files)}
                          className="hidden"
                          disabled={uploadingFiles}
                        />
                      </div>
                      
                      {/* Show uploaded police report files */}
                      {formData.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {formData.attachments.map((attachment, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 bg-white border border-blue-200 rounded-lg"
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  attachment.fileType.includes('image') 
                                    ? 'bg-purple-100' 
                                    : 'bg-red-100'
                                }`}>
                                  {attachment.fileType.includes('image') ? (
                                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  ) : (
                                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">{attachment.fileName}</p>
                                  <p className="text-xs text-gray-500">
                                    {(attachment.fileSize / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeAttachment(index);
                                }}
                                className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded-lg transition-all flex-shrink-0 ml-2"
                                title="Remove file"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Who was at fault? *</label>
                  <div className="space-y-2">
                    {[
                      'The other party',
                      "I don't know",
                      'Multiple parties',
                    ].map((option) => (
                      <label key={option} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="atFault"
                          value={option}
                          checked={formData.atFault === option}
                          onChange={(e) => updateFormData('atFault', e.target.value)}
                          className="w-4 h-4 text-aalaw-red"
                        />
                        <span className="text-sm font-medium text-gray-900">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Have you been contacted by any insurance company?</label>
                  <div className="space-y-2">
                    {[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ].map((option) => (
                      <label key={String(option.value)} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="contactedByInsurance"
                          checked={formData.contactedByInsurance === option.value}
                          onChange={() => updateFormData('contactedByInsurance', option.value)}
                          className="w-4 h-4 text-aalaw-red"
                        />
                        <span className="text-sm font-medium text-gray-900">{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {formData.contactedByInsurance && (
                    <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                      <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-red-900 font-semibold">
                        Important: Do not give a recorded statement or sign anything without speaking to an attorney first!
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Have you already hired an attorney? *</label>
                  <div className="space-y-2">
                    {[
                      { value: false, label: 'No' },
                      { value: true, label: "Yes, but I'm not satisfied" },
                    ].map((option) => (
                      <label key={String(option.value)} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="hasAttorney"
                          checked={formData.hasAttorney === option.value}
                          onChange={() => updateFormData('hasAttorney', option.value)}
                          className="w-4 h-4 text-aalaw-red"
                        />
                        <span className="text-sm font-medium text-gray-900">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Review & Submit */}
            {currentStep === 5 && (
              <div className="space-y-5 sm:space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Additional Information & Review</h2>

                {/* File Upload Section */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Upload Photos or Documents (Optional)
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Upload photos of the accident scene, injuries, medical records, or other relevant documents. 
                    Supported formats: JPEG, PNG, PDF, DOCX (Max 50MB per file)
                  </p>
                  
                  {/* Drag and Drop Area */}
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-xl p-6 sm:p-8 text-center hover:border-aalaw-red hover:bg-red-50/30 transition-all cursor-pointer bg-gray-50"
                    onClick={() => document.getElementById('file-input')?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-aalaw-red', 'bg-red-50');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('border-aalaw-red', 'bg-red-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-aalaw-red', 'bg-red-50');
                      handleFileUpload(e.dataTransfer.files);
                    }}
                  >
                    <div className="flex flex-col items-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        <svg className="w-7 h-7 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <p className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
                        {uploadingFiles ? 'Uploading...' : 'Tap to upload or drag files'}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        JPEG, PNG, PDF, DOCX up to 50MB each
                      </p>
                    </div>
                    <input
                      id="file-input"
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,application/pdf,.docx"
                      onChange={(e) => handleFileUpload(e.target.files)}
                      className="hidden"
                      disabled={uploadingFiles}
                    />
                  </div>

                  {/* Uploaded Files List */}
                  {formData.attachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {formData.attachments.length} file{formData.attachments.length > 1 ? 's' : ''} uploaded
                      </p>
                      {formData.attachments.map((attachment, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 sm:p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              attachment.fileType.includes('image') 
                                ? 'bg-purple-100' 
                                : attachment.fileType.includes('pdf')
                                  ? 'bg-red-100'
                                  : 'bg-blue-100'
                            }`}>
                              {attachment.fileType.includes('image') ? (
                                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              ) : attachment.fileType.includes('pdf') ? (
                                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900 truncate">{attachment.fileName}</p>
                              <p className="text-xs text-gray-500">
                                {(attachment.fileSize / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeAttachment(index)}
                            className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded-lg transition-all flex-shrink-0 ml-2"
                            title="Remove file"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 6: Create Account */}
            {currentStep === 6 && (
              <div className="space-y-5 sm:space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                  {isAuthenticated ? 'Review & Submit' : 'Create Your Account'}
                </h2>

                {/* Logged-in user message */}
                {isAuthenticated && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 sm:p-6">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <h3 className="font-semibold text-green-900">You're signed in as {user?.firstName} {user?.lastName}</h3>
                        <p className="text-sm text-green-700 mt-1">
                          This inquiry will be linked to your account. You'll be able to track its status and communicate with our team through your dashboard.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account Creation Option - only show for non-authenticated users */}
                {!isAuthenticated && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 sm:p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-green-900 mb-2">Set Up Your Account</h3>
                    <p className="text-sm text-green-700">
                      Set up your account now to track your inquiry status, receive updates, and easily communicate with our team.
                    </p>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <label 
                      className={`flex items-center gap-4 p-4 bg-white border-2 rounded-xl cursor-pointer transition-all ${
                        formData.createAccount 
                          ? 'border-green-500 bg-green-50 ring-2 ring-green-200' 
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="createAccount"
                        checked={formData.createAccount === true}
                        onChange={() => updateFormData('createAccount', true)}
                        className="w-5 h-5 text-green-600"
                      />
                      <div className="flex-1">
                        <span className={`text-base font-semibold ${formData.createAccount ? 'text-green-700' : 'text-gray-900'}`}>
                          Yes, create my account now
                        </span>
                        <p className="text-sm text-green-600 font-medium">Recommended • Get instant access</p>
                      </div>
                      {formData.createAccount && (
                        <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </label>
                    
                    <label 
                      className={`flex items-center gap-4 p-4 bg-white border-2 rounded-xl cursor-pointer transition-all ${
                        !formData.createAccount 
                          ? 'border-gray-400 bg-gray-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="createAccount"
                        checked={formData.createAccount === false}
                        onChange={() => updateFormData('createAccount', false)}
                        className="w-5 h-5 text-gray-600"
                      />
                      <div className="flex-1">
                        <span className={`text-base font-semibold ${!formData.createAccount ? 'text-gray-700' : 'text-gray-900'}`}>
                          Skip for now
                        </span>
                        <p className="text-sm text-gray-500">Create account later</p>
                      </div>
                    </label>
                  </div>

                  {formData.createAccount && (
                    <div className="space-y-6 pt-6 border-t border-green-200">
                      {/* Account Details Section */}
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="font-semibold text-gray-900">Confirm Your Account Details</span>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                            <Input
                              value={formData.firstName}
                              onChange={(e) => updateFormData('firstName', e.target.value)}
                              placeholder="First name"
                              className={`w-full ${!formData.firstName.trim() ? 'border-red-300' : 'border-green-300'}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                            <Input
                              value={formData.lastName}
                              onChange={(e) => updateFormData('lastName', e.target.value)}
                              placeholder="Last name"
                              className={`w-full ${!formData.lastName.trim() ? 'border-red-300' : 'border-green-300'}`}
                            />
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                          <div className="flex gap-2">
                            <Input
                              type="email"
                              value={formData.email}
                              onChange={(e) => updateFormData('email', e.target.value)}
                              onBlur={handleCheckEmail}
                              placeholder="your@email.com"
                              className={`w-full ${
                                emailExists === true
                                  ? 'border-amber-400'
                                  : !formData.email.trim() || (formData.email && !isValidEmail(formData.email))
                                    ? 'border-red-300' 
                                    : 'border-green-300'
                              }`}
                            />
                            {checkingEmail && (
                              <div className="flex items-center px-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                              </div>
                            )}
                          </div>
                          {emailExists === true ? (
                            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800 font-medium">
                                An account with this email already exists.
                              </p>
                              <p className="text-sm text-amber-700 mt-1">
                                <Link to="/signin" className="text-amber-900 underline font-semibold hover:text-amber-700">
                                  Sign in
                                </Link>
                                {' '}to link this inquiry to your existing account, or use a different email.
                              </p>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-600 mt-1">This will be your login email</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                          <Input
                            type="tel"
                            value={formData.phoneNumber}
                            onChange={handlePhoneChange}
                            placeholder="(555) 123-4567"
                            className={`w-full ${!formData.phoneNumber.trim() ? 'border-red-300' : 'border-green-300'}`}
                          />
                        </div>
                      </div>

                      {/* Password Section */}
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="font-semibold text-gray-900">Create Your Password</span>
                        </div>
                        
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Password *</label>
                          <Input
                            type="password"
                            value={formData.password}
                            onChange={(e) => updateFormData('password', e.target.value)}
                            placeholder="Create a secure password"
                            className={`w-full ${
                              formData.password && !validatePassword(formData.password).isValid
                                ? 'border-red-300' 
                                : formData.password && validatePassword(formData.password).isValid
                                  ? 'border-green-300' 
                                  : ''
                            }`}
                          />
                          {/* Password requirements checklist */}
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs font-semibold text-gray-700 mb-2">Password must contain:</p>
                            <div className="grid grid-cols-2 gap-1">
                              {[
                                { check: formData.password.length >= 8, label: 'At least 8 characters' },
                                { check: /[A-Z]/.test(formData.password), label: 'One uppercase letter' },
                                { check: /[a-z]/.test(formData.password), label: 'One lowercase letter' },
                                { check: /[0-9]/.test(formData.password), label: 'One number' },
                                { check: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password), label: 'One special character' },
                              ].map((req, idx) => (
                                <div key={idx} className="flex items-center gap-1.5">
                                  {formData.password ? (
                                    req.check ? (
                                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                      </svg>
                                    )
                                  ) : (
                                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                                  )}
                                  <span className={`text-xs ${formData.password && req.check ? 'text-green-700' : 'text-gray-600'}`}>
                                    {req.label}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password *</label>
                          <Input
                            type="password"
                            value={formData.confirmPassword}
                            onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                            placeholder="Re-enter password"
                            className={`w-full ${
                              formData.confirmPassword && formData.password !== formData.confirmPassword 
                                ? 'border-red-300' 
                                : formData.confirmPassword && formData.password === formData.confirmPassword && validatePassword(formData.password).isValid
                                  ? 'border-green-300' 
                                  : ''
                            }`}
                          />
                          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Passwords don't match
                            </p>
                          )}
                          {formData.confirmPassword && formData.password === formData.confirmPassword && validatePassword(formData.password).isValid && (
                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Passwords match
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Benefits Section - shown when creating account */}
                  {formData.createAccount && (
                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <h4 className="font-semibold text-amber-900 mb-3">What you'll get</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-amber-900">Instant Access</p>
                            <p className="text-xs text-amber-700">Log in immediately</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-amber-900">Track Inquiry</p>
                            <p className="text-xs text-amber-700">Real-time updates</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-amber-900">Easy Communication</p>
                            <p className="text-xs text-amber-700">Message us directly</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-amber-900">Upload Documents</p>
                            <p className="text-xs text-amber-700">Add files anytime</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Skip Notice - shown when not creating account */}
                  {!formData.createAccount && (
                    <div className="flex items-start gap-3 p-4 bg-gray-100 rounded-xl">
                      <svg className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-gray-600">
                        No problem! You can create an account later when our team contacts you about your inquiry.
                      </p>
                    </div>
                  )}
                </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    What Happens Next?
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-blue-900">We'll Contact You Soon</p>
                        <p className="text-sm text-blue-800">Our intake team will reach out to discuss your case</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-blue-900">Free Case Evaluation</p>
                        <p className="text-sm text-blue-800">We'll review your case at no cost to you</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-blue-900">No Win, No Fee</p>
                        <p className="text-sm text-blue-800">You don't pay unless we win your case</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    Your Information
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-semibold text-gray-700">Name:</span>{' '}
                      <span className="text-gray-900">{formData.firstName} {formData.lastName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="font-semibold text-gray-700">Phone:</span>{' '}
                      <span className="text-gray-900">{formData.phoneNumber}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-semibold text-gray-700">Email:</span>{' '}
                      <span className="text-gray-900">{formData.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="font-semibold text-gray-700">Incident Type:</span>{' '}
                      <span className="text-gray-900">{formData.incidentType}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="font-semibold text-gray-700">Location:</span>{' '}
                      <span className="text-gray-900">{formData.incidentLocation}</span>
                    </div>
                    {formData.attachments.length > 0 && (
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="font-semibold text-gray-700">Attachments:</span>{' '}
                        <span className="text-gray-900">{formData.attachments.length} file(s) uploaded</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-amber-900">
                    <strong>Note:</strong> Submitting this form does not create an attorney-client relationship. 
                    We will review your case and contact you to discuss next steps.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="w-full sm:flex-1 py-3 sm:py-2"
                >
                  ← Previous
                </Button>
              )}
              {currentStep < 6 ? (
                <Button
                  onClick={nextStep}
                  disabled={!canProceed()}
                  style={{ backgroundColor: '#C41E3A', color: '#FFFFFF' }}
                  className="w-full sm:flex-1 py-3 sm:py-2 hover:bg-red-700"
                >
                  Next →
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ backgroundColor: '#C41E3A', color: '#FFFFFF' }}
                  className="w-full sm:flex-1 py-3 sm:py-2 hover:bg-red-700"
                >
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trust Badges */}
        <div className="mt-6 sm:mt-8 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 md:gap-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Secure & Confidential</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Fast Response</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-aalaw-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>No Win, No Fee</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
